import React, { useEffect, useState } from "react";
import { advanceDevClock, getDevClock, reseedDevClock, resetDevClock, setDevClock } from "../../../api/devClockApi";
import { ConfirmDialog, EnvBadge, Notice, PageHeader, Panel, StatePanel } from "../ui/AdminUi";
import { I } from "../ui/icons";

const MOVES = [
  { label: "+1 day", days: 1 },
  { label: "+7 days", days: 7 },
  { label: "+30 days", days: 30 },
  { label: "+1 year", days: 365 },
];
const BACKWARDS = [
  { label: "−1 day", days: -1 },
  { label: "−7 days", days: -7 },
  { label: "−30 days", days: -30 },
];

/** The clock counts as moved when it is more than a minute away from real time. */
const MOVED_AFTER_MS = 60 * 1000;

/**
 * The backend sends nanosecond precision (for example 2026-10-11T03:20:09.138912400Z). Browsers parse milliseconds
 * reliably, so the fraction is cut to three digits before the date is read.
 */
export function parseInstant(instant) {
  if (!instant) return null;
  const date = new Date(String(instant).replace(/(\.\d{3})\d+/, "$1"));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Moves the Local environment's clock to test expirations, reminders and scheduled jobs. The simulated time is shown
 * prominently while it differs from real time, and reset is the action offered at that point. Only exists in Local.
 */
export default function TimeTravelView({ env }) {
  const [clock, setClock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState("");
  const [reseeding, setReseeding] = useState(false);

  const refresh = () => {
    setLoading(true);
    getDevClock()
      .then((data) => { setClock(data); setUnavailable(false); setError(null); })
      .catch((err) => {
        if (err?.response?.status === 404) setUnavailable(true);
        else setError(err?.message || "Failed to load the dev clock.");
      })
      .finally(() => setLoading(false));
  };

  // The dev clock only exists in Local: nothing is requested from any other environment.
  useEffect(() => { if (env === "local") refresh(); }, [env]);

  const run = (action) => {
    setError(null);
    action()
      .then((data) => setClock(data))
      .catch((err) => setError(err?.message || "Action failed."));
  };

  const header = <PageHeader title="Time travel" desc="Move the Local environment's clock to test date-dependent behaviour." />;

  if (env !== "local") {
    return (
      <>
        {header}
        <Panel><StatePanel kind="denied" why={`Time travel only exists in the Local environment. You are in ${env === "dev" ? "Dev" : "Production"}, where the clock always follows real time.`} /></Panel>
      </>
    );
  }

  if (loading && !clock) {
    return <>{header}<Panel><StatePanel kind="loading" what="dev clock" /></Panel></>;
  }

  if (unavailable) {
    return <>{header}<Notice kind="info">The dev clock isn't available. This backend isn't running under the local profile.</Notice></>;
  }

  const simulated = parseInstant(clock?.instant);
  const moved = simulated && Math.abs(simulated.getTime() - Date.now()) > MOVED_AFTER_MS;

  return (
    <>
      <PageHeader
        title="Time travel"
        desc="Move the Local environment's clock to test expirations, reminders and scheduled jobs, then return to real time."
        actions={moved ? <button type="button" className="b pri" onClick={() => run(resetDevClock)}><I.refresh s={14} />Reset to real time</button> : null}
      />

      {moved && (
        <div style={{ marginBottom: 16 }}>
          <Notice kind="info">
            The Local clock is set to {simulated.toLocaleString()}. Scheduled jobs run as if it is that time until you reset.
          </Notice>
        </div>
      )}
      {error && <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div>}

      <div className="g2" style={{ alignItems: "start" }}>
        <Panel title="Clock" actions={<EnvBadge env="local" />}>
          <div className="panel-b" style={{ display: "grid", gap: 16 }}>
            <div>
              <div className="eyebrow">Simulated time</div>
              <div style={{ font: "700 24px/1.2 var(--font-display)", marginTop: 6, color: moved ? "var(--env-local)" : "var(--color-ink)" }}>
                {simulated ? simulated.toLocaleString() : "—"}
              </div>
              <div className="sub" style={{ marginTop: 4, fontSize: 13 }}>{moved ? "Moved away from real time" : "Following real time"}</div>
            </div>
            <div>
              <div className="eyebrow">Real time</div>
              <div className="mono" style={{ marginTop: 6 }}>{new Date().toLocaleString()}</div>
            </div>
          </div>
        </Panel>

        <Panel title="Move the clock">
          <div className="panel-b" style={{ display: "grid", gap: 16 }}>
            <div className="field">
              Forward
              <div className="tb">
                {MOVES.map((mv) => <button key={mv.label} type="button" className="fchip" onClick={() => run(() => advanceDevClock({ days: mv.days }))}>{mv.label}</button>)}
              </div>
            </div>
            <div className="field">
              Back
              <div className="tb">
                {BACKWARDS.map((mv) => <button key={mv.label} type="button" className="fchip" onClick={() => run(() => advanceDevClock({ days: mv.days }))}>{mv.label}</button>)}
              </div>
            </div>
            <label className="field">
              Jump to a date and time
              <span style={{ display: "flex", gap: 8 }}>
                <span className="inp" style={{ flex: 1 }}>
                  <input type="datetime-local" value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Jump to date and time" />
                </span>
                <button type="button" className="b sec" disabled={!target} onClick={() => run(() => setDevClock(new Date(target).toISOString()))}><I.clock s={14} />Jump</button>
              </span>
            </label>
          </div>
        </Panel>
      </div>

      <Panel
        title="Reseed data"
        subtitle="Wipes the Local database and loads the sample data again."
        style={{ marginTop: 16 }}
        actions={<button type="button" className="b dsec" onClick={() => setReseeding(true)}>Reseed data…</button>}
      >
        <div className="panel-b sub" style={{ fontSize: 13.5 }}>Local data you created is lost. Dev and Production are not affected.</div>
      </Panel>

      <ConfirmDialog
        open={reseeding}
        env="local"
        title="Wipe and reseed the Local database?"
        consequence="Every record in the Local database is deleted and replaced with the sample data."
        keeps="Dev and Production are not changed."
        typed="reseed"
        confirmLabel="Wipe and reseed"
        onClose={() => setReseeding(false)}
        onConfirm={() => { setReseeding(false); run(reseedDevClock); }}
      />
    </>
  );
}
